import React from 'react';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { MessageSquare, CheckCircle2, Clock } from 'lucide-react';
import { fmtDate } from './attachments';

export function DiscussionListDialog({ open, onOpenChange, discussions, onSelect }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass-card border-border/50 rounded-2xl sm:max-w-md max-h-[70vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <MessageSquare className="w-4 h-4 text-amber-400" /> Discussion Threads ({discussions.length})
          </DialogTitle>
        </DialogHeader>
        <ScrollArea className="flex-1 mt-2">
          <div className="space-y-2">
            {discussions.map(d => (
              <button key={d.discussion_id} onClick={() => { onSelect(d.discussion_id); onOpenChange(false); }}
                className="w-full text-left p-3 rounded-xl border border-border/30 hover:border-primary/30 hover:bg-primary/5 transition-all active:scale-[0.98]"
                data-testid={`disc-list-${d.discussion_id}`}>
                <div className="flex items-start justify-between gap-2 mb-1.5">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center text-[9px] font-bold text-primary flex-shrink-0">{d.created_by_name?.[0] || 'U'}</div>
                    <span className="text-[11px] font-semibold text-foreground">{d.created_by_name}</span>
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    {d.unread_replies > 0 && <Badge className="text-[7px] bg-blue-500 text-white border-blue-600" data-testid={`disc-unread-${d.discussion_id}`}>{d.unread_replies} new</Badge>}
                    {d.status === 'resolved'
                      ? <Badge className="text-[7px] bg-emerald-600 text-white border-emerald-700"><CheckCircle2 className="w-2 h-2 mr-0.5" />Resolved</Badge>
                      : <Badge className="text-[7px] bg-amber-500 text-white border-amber-600">Open</Badge>}
                    {(d.replies?.length || 0) > 0 && <Badge variant="outline" className="text-[7px]">{d.replies.length} replies</Badge>}
                  </div>
                </div>
                <p className="text-xs text-foreground/80 line-clamp-2 mb-1.5">{d.question}</p>
                {d.data_context && Object.keys(d.data_context).length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-1">
                    {Object.entries(d.data_context).filter(([k]) => k !== '_row_count').slice(0, 3).map(([k, v]) => (
                      <Badge key={k} variant="outline" className="text-[7px] font-mono">{k}: {String(v).substring(0, 20)}</Badge>
                    ))}
                  </div>
                )}
                <div className="flex items-center gap-1 text-[9px] text-muted-foreground">
                  <Clock className="w-2.5 h-2.5" /> {fmtDate(d.created_at)}
                </div>
              </button>
            ))}
            {discussions.length === 0 && <p className="text-sm text-muted-foreground text-center py-8">No discussions yet</p>}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}

export function DiscussionIndicator({ discussions, dataPoint }) {
  if (!discussions?.length || !dataPoint) return null;
  const matching = discussions.filter(d => {
    const ctx = d.data_context || {};
    return Object.entries(ctx).some(([k, v]) => dataPoint[k] !== undefined && String(dataPoint[k]) === String(v));
  });
  if (matching.length === 0) return null;
  return (
    <div className="flex items-center gap-1 mt-1 text-[9px] text-amber-600 dark:text-amber-400 font-semibold">
      <MessageSquare className="w-2.5 h-2.5" />
      <span>{matching.length} discussion{matching.length > 1 ? 's' : ''}</span>
    </div>
  );
}
